import {
    useEffect,
    useMemo,
    useState,
} from "react";

import ExcelJS from "exceljs";

import {
    createColumnHelper,
} from "@tanstack/react-table";

import DataTable from "components/table/DataTable";

import {
    showError,
} from "utils/toast";

import {
    REPORTS,
} from "./constants";

import {
    reportColumns,
} from "./columns";

import StatementModal from "./StatementModal";

import StatementHistory from "./StatementHistory";

import ExpenseModal from "../expenses/ExpenseModal";

import {
    getDailyWorkingReport,
    getDailyWorkingSummary,
    getDailyWorkingDetails,
    getVisitReport,
    getExpenseReport,
    getTaDaReport,
    getPaymentReport,
    getCollectionReport,
    getAppraisalReport,
    getSampleReport,
    getAttendanceReport,
    getGpsComplianceReport,
    getJourneyComplianceReport,
    getSalesPersonPerformanceReport,
} from "services/api/reportsApi";

import {
    getEmployees,
} from "services/api/employeeApi";

import {
    approveExpense,
} from "services/api/expenseApi";

import {
    getJourneyPlans,
} from "services/api/journeyPlanApi";

import {
    getVisits,
} from "services/api/visitApi";


const columnHelper = createColumnHelper();


/*
|--------------------------------------------------------------------------
| DEFAULT FILTERS
|--------------------------------------------------------------------------
*/

const DEFAULT_FILTERS = {
    fromDate: "",
    toDate: "",
    employeeId: "",
    journeyPlanItemId: "",
    visitId: "",
    schoolId: "",
    partyId: "",
    status: "",
    expenseType: "",
    approvalStatus: "",
};


/*
|--------------------------------------------------------------------------
| OTHER REPORT APIs
|--------------------------------------------------------------------------
| Daily Working is handled separately because it has:
| Summary -> Details flow.
|--------------------------------------------------------------------------
*/

const REPORT_FUNCTIONS = {
    visits: getVisitReport,
    expenses: getExpenseReport,
    ta_da: getTaDaReport,
    payments: getPaymentReport,
    collections: getCollectionReport,
    appraisals: getAppraisalReport,
    samples: getSampleReport,
    attendance: getAttendanceReport,
    gps_compliance: getGpsComplianceReport,
    journey_compliance: getJourneyComplianceReport,
    sales_person_performance: getSalesPersonPerformanceReport,
};


const INPUT_CLASS =
    "mt-1 h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 dark:border-navy-600 dark:bg-navy-800 dark:text-white";


const LABEL_CLASS =
    "block text-sm font-medium text-gray-700 dark:text-gray-200";


/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

function getList(
    response,
    possibleKeys = []
) {
    const candidates = [
        response?.data?.data,
        response?.data,
        response,
    ];

    for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
            return candidate;
        }

        for (const key of possibleKeys) {
            if (Array.isArray(candidate?.[key])) {
                return candidate[key];
            }
        }
    }

    return [];
}


function getEmployeeName(employee) {
    return (
        employee?.user?.name ||
        employee?.name ||
        employee?.fullName ||
        employee?.employeeName ||
        employee?.user?.fullName ||
        `Employee ${employee?.id ?? ""}`.trim()
    );
}


function getJourneyName(journey) {
    return (
        journey?.journeyName ||
        journey?.name ||
        journey?.title ||
        journey?.planName ||
        journey?.school?.name ||
        journey?.party?.name ||
        journey?.planTitle ||
        `Journey ${journey?.id ?? ""}`.trim()
    );
}


function getVisitName(visit) {
    return (
        visit?.visitName ||
        visit?.name ||
        visit?.school?.name ||
        visit?.party?.name ||
        visit?.purpose ||
        visit?.title ||
        `Visit ${visit?.id ?? ""}`.trim()
    );
}


function getJourneyItems(plans) {
    return plans.flatMap((plan) =>
        (
            Array.isArray(plan?.items)
                ? plan.items
                : []
        ).map((item) => ({
            ...item,
            id: String(item.id),
            planId: String(plan.id),
            planTitle:
                plan.title ||
                plan.name ||
                plan.journeyName ||
                "",
        }))
    );
}


function formatDate(value) {
    if (!value) {
        return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return date.toLocaleDateString(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    );
}


function formatTime(value) {
    if (!value) {
        return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    return date.toLocaleTimeString(
        "en-IN",
        {
            hour: "2-digit",
            minute: "2-digit",
        }
    );
}


function formatAmount(value) {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "₹0.00";
    }

    const number = Number(value);

    return Number.isFinite(number)
        ? `₹${number.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
          })}`
        : String(value);
}


function displayValue(value) {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "-";
    }

    if (typeof value === "object") {
        return (
            value.name ||
            value.title ||
            value.label ||
            "-"
        );
    }

    return String(value);
}


function getFirstValue(...values) {
    return values.find(
        (value) => value !== null && value !== undefined && value !== ""
    );
}

function getVisitCoordinates(row) {
    const visit =
        row?.latestVisit ||
        row?.currentVisit ||
        row?.lastVisit ||
        row?.visit ||
        row?.visits?.[0] ||
        {};

    return {
        visit,
        latitude: getFirstValue(
            visit?.latitude,
            visit?.lat,
            row?.latitude,
            row?.lat,
            row?.currentLatitude,
            row?.currentLocation?.latitude
        ),
        longitude: getFirstValue(
            visit?.longitude,
            visit?.lng,
            visit?.long,
            row?.longitude,
            row?.lng,
            row?.currentLongitude,
            row?.currentLocation?.longitude
        ),
    };
}

// Cache reverse-geocoded addresses so the same GPS coordinates are
// not requested repeatedly while the report table re-renders.
const reverseGeocodeCache = new Map();

function ReverseGeocodedLocation({
    latitude,
    longitude,
    fallback = "",
}) {
    const [address, setAddress] = useState(fallback || "");

    useEffect(() => {
        let cancelled = false;

        const lat = Number(latitude);
        const lon = Number(longitude);

        if (
            latitude === null ||
            latitude === undefined ||
            latitude === "" ||
            longitude === null ||
            longitude === undefined ||
            longitude === "" ||
            !Number.isFinite(lat) ||
            !Number.isFinite(lon)
        ) {
            setAddress(fallback || "");
            return undefined;
        }

        if (fallback) {
            setAddress(String(fallback));
        }

        const cacheKey = `${lat.toFixed(6)},${lon.toFixed(6)}`;
        const cached = reverseGeocodeCache.get(cacheKey);

        if (cached) {
            cached.then((value) => {
                if (!cancelled && value) setAddress(value);
            });
            return () => {
                cancelled = true;
            };
        }

        const request = (async () => {
            try {
                const response = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}&zoom=18&addressdetails=1`,
                    { headers: { Accept: "application/json" } }
                );

                if (!response.ok) return "";

                const data = await response.json();
                const resolved =
                    data?.display_name ||
                    [
                        data?.address?.road,
                        data?.address?.neighbourhood,
                        data?.address?.suburb,
                        data?.address?.city ||
                            data?.address?.town ||
                            data?.address?.village,
                        data?.address?.state,
                        data?.address?.postcode,
                    ].filter(Boolean).join(", ");

                return resolved || "";
            } catch (error) {
                console.warn("Reverse geocoding failed:", error);
                return "";
            }
        })();

        reverseGeocodeCache.set(cacheKey, request);

        request.then((value) => {
            if (!cancelled && value) setAddress(value);
        });

        return () => {
            cancelled = true;
        };
    }, [latitude, longitude, fallback]);

    const hasCoordinates =
        latitude !== null &&
        latitude !== undefined &&
        latitude !== "" &&
        longitude !== null &&
        longitude !== undefined &&
        longitude !== "";

    if (!hasCoordinates) {
        return <>{address || "-"}</>;
    }

    return (
        <a
            href={`https://www.google.com/maps?q=${latitude},${longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Open this location in Google Maps"
            className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
        >
            {address || "Resolving location..."}
        </a>
    );
}


function formatStatus(value) {
    if (!value) {
        return "-";
    }

    return String(value)
        .replace(/_/g, " ")
        .replace(
            /\b\w/g,
            (char) => char.toUpperCase()
        );
}


/*
|--------------------------------------------------------------------------
| VISIT COLUMNS
|--------------------------------------------------------------------------
| Existing Visit report continues to use DataTable.
|--------------------------------------------------------------------------
*/

const VISIT_COLUMNS = [
    columnHelper.accessor(
        "visitDate",
        {
            id: "visitDate",
            header: "Visit Date",
            cell: (info) =>
                formatDate(
                    info.getValue()
                ),
        }
    ),

    columnHelper.accessor(
        (row) =>
            row?.employee?.name ??
            row?.employee?.user?.name,
        {
            id: "employeeName",
            header: "Employee",
            cell: (info) =>
                displayValue(
                    info.getValue()
                ),
        }
    ),

    columnHelper.accessor(
        "employee.employeeCode",
        {
            id: "employeeCode",
            header: "Employee Code",
            cell: (info) =>
                displayValue(
                    info.getValue()
                ),
        }
    ),

    columnHelper.accessor(
        "visitType",
        {
            id: "visitType",
            header: "Visit Type",
            cell: (info) =>
                displayValue(
                    info.getValue()
                ),
        }
    ),

    columnHelper.accessor(
        (row) =>
            row?.visitName ??
            row?.school?.name ??
            row?.party?.name ??
            row?.purpose,
        {
            id: "visitDetails",
            header: "Visit Details",
            cell: (info) =>
                displayValue(
                    info.getValue()
                ),
        }
    ),

    columnHelper.accessor(
        "purpose",
        {
            id: "purpose",
            header: "Purpose",
            cell: (info) =>
                displayValue(
                    info.getValue()
                ),
        }
    ),

    columnHelper.accessor(
        "status",
        {
            id: "status",
            header: "Status",
            cell: (info) =>
                formatStatus(
                    info.getValue()
                ),
        }
    ),
];


/*
|--------------------------------------------------------------------------
| DAILY WORKING DETAIL VISIT COLUMNS
|--------------------------------------------------------------------------
*/

const DAILY_VISIT_COLUMNS = [
    {
        key: "visitDate",
        label: "Visit Date",
        render: (visit) => formatDate(visit?.visitDate),
    },
    {
        key: "visitNumber",
        label: "Visit No.",
        render: (visit) => displayValue(visit?.visitNumber),
    },
    {
        key: "visitType",
        label: "Visit Type",
        render: (visit) => formatStatus(visit?.visitType),
    },
    {
    key: "visitLocation",
    label: "Visit Location",
    render: (visit) =>
        displayValue(
            visit?.school?.city?.name ||
            visit?.visitLocation ||
            visit?.address
        ),
},
    {
        key: "school",
        label: "School Name",
        render: (visit) => displayValue(visit?.school?.name),
    },
    {
        key: "schoolCode",
        label: "School Code",
        render: (visit) => displayValue(visit?.school?.schoolCode),
    },
    {
        key: "party",
        label: "Party Name",
        render: (visit) =>
            displayValue(visit?.party?.name ?? visit?.partyName),
    },
    {
        key: "journey",
        label: "Journey",
        render: (visit) => displayValue(visit?.journey?.name),
    },
    {
    key: "personMet",
    label: "PERSON MET",
    render: (visit) =>
        displayValue(visit?.personMet),
},
{
    key: "designation",
    label: "DESIGNATION",
    render: (visit) =>
        displayValue(visit?.designation),
},
{
    key: "contactNumber",
    label: "CONTACT NUMBER",
    render: (visit) =>
        displayValue(visit?.contactNumber),
},
{
    key: "followUp",
    label: "FOLLOW-UP / NEXT ACTION",
    render: (visit) =>
        displayValue(visit?.followUp),
},
    {
        key: "purpose",
        label: "Purpose",
        render: (visit) => displayValue(visit?.purpose),
    },
    {
        key: "assessment",
        label: "Assessment",
        render: (visit) => displayValue(visit?.assessment),
    },
    {
        key: "remarks",
        label: "Remarks",
        render: (visit) => displayValue(visit?.remarks),
    },
    {
        key: "startAt",
        label: "Start Time",
        render: (visit) => formatTime(visit?.startAt),
    },
    {
        key: "endAt",
        label: "End Time",
        render: (visit) => formatTime(visit?.endAt),
    },
    {
        key: "totalKm",
        label: "Total KM",
        render: (visit) =>
            visit?.totalKm != null
                ? `${visit.totalKm} KM`
                : "-",
    },
    
{
    key: "location",
    label: "Location",
    render: (visit) => {
        const address = visit?.address;

        const latitude = visit?.latitude;
        const longitude = visit?.longitude;

        if (address) {
            return address;
        }

        if (
            latitude != null &&
            longitude != null
        ) {
            const mapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;

            return (
                <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-blue-600 underline hover:text-blue-800"
                >
                    View Location
                </a>
            );
        }

        return "-";
    },
},
    {
        key: "address",
        label: "Address",
        render: (visit) => displayValue(visit?.address),
    },
    {
        key: "claimedExpense",
        label: "Claimed Expense",
        render: (visit) => formatAmount(visit?.claimedExpense),
    },
    {
        key: "approvedExpense",
        label: "Approved Expense",
        render: (visit) => formatAmount(visit?.approvedExpense),
    },
    {
        key: "status",
        label: "Status",
        render: (visit) => formatStatus(visit?.status),
    },
];



/*
|--------------------------------------------------------------------------
| DAILY EXPENSE DETAIL COLUMNS
|--------------------------------------------------------------------------
*/

const DAILY_EXPENSE_COLUMNS = [
    {
        key: "expenseDate",
        label: "Date",
        render: (expense) =>
            formatDate(
                expense?.expenseDate
            ),
    },

    {
        key: "expenseType",
        label: "Expense Type",
        render: (expense) =>
            formatStatus(
                expense?.expenseType
            ),
    },

    {
        key: "title",
        label: "Title",
        render: (expense) =>
            displayValue(
                expense?.title
            ),
    },

    {
        key: "fromLocation",
        label: "From",
        render: (expense) =>
            displayValue(
                expense?.fromLocation
            ),
    },

    {
        key: "toLocation",
        label: "To",
        render: (expense) =>
            displayValue(
                expense?.toLocation
            ),
    },

    {
        key: "distanceKm",
        label: "Distance",
        render: (expense) =>
            expense?.distanceKm != null
                ? `${expense.distanceKm} KM`
                : "-",
    },

    {
        key: "claimedAmount",
        label: "Claimed",
        render: (expense) =>
            formatAmount(
                expense?.claimedAmount
            ),
    },

    {
        key: "approvedAmount",
        label: "Approved",
        render: (expense) =>
            formatAmount(
                expense?.approvedAmount
            ),
    },

    {
        key: "status",
        label: "Status",
        render: (expense) =>
            formatStatus(
                expense?.status
            ),
    },
    {
        key: "approvedByRole",
        label: "Approval Role",
        render: (expense) => {
            const role = String(expense?.approvedByRole ?? "")
                .trim()
                .toUpperCase()
                .replace(/[ -]+/g, "_");

            if (["ADMIN", "SUPER_ADMIN", "SUPERADMIN"].includes(role)) {
                return <span className="font-semibold text-green-700">Admin Approved</span>;
            }

            if (["TEAM_LEADER", "TL"].includes(role)) {
                return <span className="font-semibold text-amber-700">Team Leader Review</span>;
            }

            return <span className="font-medium text-red-700">Pending</span>;
        },
    },
    {
        key: "teamLeaderRemark",
        label: "Team Leader Remark",
        render: (expense) => displayValue(expense?.teamLeaderRemark),
    },
    {
        key: "adminRemark",
        label: "Admin Remark",
        render: (expense) => displayValue(expense?.adminRemark),
    },
    {
        key: "files",
        label: "Attachments",
        render: (expense) => {
            const files = Array.isArray(expense?.files) ? expense.files : [];
            if (!files.length) return "-";

            // Set REACT_APP_API_URL to the backend API base URL, e.g.
            // http://localhost:5000/api/v1. Static uploads are served from backend root.
            const configuredApiUrl =
                process.env.REACT_APP_API_URL ||
                process.env.REACT_APP_API_BASE_URL ||
                "http://localhost:5000/api/v1";
            const backendOrigin = configuredApiUrl
                .replace(/\/+$/, "")
                .replace(/\/api\/v1$/i, "");

            return (
                <div className="flex min-w-[140px] flex-col gap-1">
                    {files.map((file, index) => {
                        const path = file?.filePath ?? file?.fileUrl ?? file?.url ?? file?.path;
                        if (!path) return null;

                        let href;
                        try {
                            const value = String(path).trim();
                            if (/^(https?:|blob:|data:)/i.test(value)) {
                                href = value;
                            } else {
                                const normalized = value
                                    .replace(/\\/g, "/")
                                    .replace(/^\/+/, "");

                                // Uploads are served by the backend at /uploads/.
                                // Accept "receipt.jpg", "uploads/receipt.jpg",
                                // "/uploads/receipt.jpg", and "api/v1/uploads/receipt.jpg".
                                const filename = normalized
                                    .replace(/^api\/v1\/uploads\//i, "")
                                    .replace(/^uploads\//i, "");

                                href = `${backendOrigin}/uploads/${filename}`;
                            }
                        } catch (error) {
                            console.error("Invalid expense attachment path:", path, error);
                            return null;
                        }

                        return (
                            <a
                                key={file?.id ?? `${path}-${index}`}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={`Open ${file?.fileName || file?.caption || "expense attachment"}`}
                                className="font-medium text-blue-600 underline hover:text-blue-800"
                            >
                                {file?.caption || file?.fileName || `Attachment ${index + 1}`}
                            </a>
                        );
                    })}
                </div>
            );
        },
    },
    {
        key: "actions",
        label: "Action",
        render: () => null,
    },
];


/*
|--------------------------------------------------------------------------
| SIMPLE TABLE COMPONENT
|--------------------------------------------------------------------------
*/

function SimpleTable({
    columns,
    rows,
    emptyText,
    canEditExpenses = false,
    onEditExpense,
    onApproveExpense,
}) {
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
                <thead>
                    <tr className="border-b border-gray-200 bg-gray-50 dark:border-navy-700 dark:bg-navy-900">
                        {columns.map(
                            (column) => (
                                <th
                                    key={
                                        column.key
                                    }
                                    className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                                >
                                    {
                                        column.label
                                    }
                                </th>
                            )
                        )}
                    </tr>
                </thead>

                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td
                                colSpan={
                                    columns.length
                                }
                                className="px-4 py-8 text-center text-gray-500"
                            >
                                {
                                    emptyText
                                }
                            </td>
                        </tr>
                    ) : (
                        rows.map(
                            (
                                row,
                                index
                            ) => (
                                <tr
                                    key={
                                        String(
                                            row?.id ??
                                                index
                                        )
                                    }
                                    className={`border-b border-gray-100 dark:border-navy-700 ${
    String(row?.status ?? "").toLowerCase() === "approved"
        ? "bg-green-50 dark:bg-green-950/20"
        : "bg-red-50 dark:bg-red-950/20"
}`}
                                >
                                    {columns.map(
                                        (
                                            column
                                        ) => (
                                            <td
                                                key={
                                                    column.key
                                                }
                                                className="whitespace-nowrap px-4 py-3 text-gray-700 dark:text-gray-200"
                                            >
                                                {column.key === "actions" ? (
                                                    canEditExpenses ? (
                                                        <div className="flex items-center gap-2">
                                                            {String(row?.status ?? "").toLowerCase() === "submitted" && (
                                                                <button
                                                                    type="button"
                                                                    title="Approve expense"
                                                                    onClick={() => onApproveExpense?.(row)}
                                                                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-green-200 bg-green-50 text-green-700 transition hover:bg-green-100"
                                                                >
                                                                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                        <path d="m5 12 4 4L19 6" />
                                                                    </svg>
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                title="Edit expense"
                                                                onClick={() => onEditExpense?.(row)}
                                                                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 transition hover:bg-blue-100"
                                                            >
                                                                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                                    <path d="M12 20h9" />
                                                                    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4Z" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400">-</span>
                                                    )
                                                ) : column.render ? (
                                                    column.render(row)
                                                ) : (
                                                    displayValue(row?.[column.key])
                                                )}
                                            </td>
                                        )
                                    )}
                                </tr>
                            )
                        )
                    )}
                </tbody>
            </table>
        </div>
    );
}


/*
|--------------------------------------------------------------------------
| DAILY WORKING DETAILS
|--------------------------------------------------------------------------
*/

function DailyWorkingDetails({
    details,
    loading,
    onBack,
    canEditExpenses = false,
    onEditExpense,
    onApproveExpense,
    onApproveAllExpenses,
    bulkApproving = false,
}) {
    if (loading) {
        return (
            <section className="rounded-xl border border-gray-200 bg-white p-10 text-center shadow-sm dark:border-navy-700 dark:bg-navy-800">
                <div className="text-sm font-medium text-gray-500">
                    Loading daily working details...
                </div>
            </section>
        );
    }

    if (!details) {
        return null;
    }

    const employee =
        details?.employee || {};

    const visits =
        Array.isArray(details?.visits)
            ? details.visits
            : [];

    const expenses =
        Array.isArray(details?.expenses)
            ? details.expenses
            : [];

    const summary =
        details?.summary || {};

    const approval =
        details?.approval || {};

    const approved =
    details?.approval?.allExpensesApproved ??
    details?.approval?.status === "approved";

    const pendingExpenses = expenses.filter(
        (expense) =>
            String(expense?.status ?? "").trim().toLowerCase() === "submitted"
    );

    const exportDailyDetailsExcel = async () => {
    try {
        const workbook = new ExcelJS.Workbook();

        workbook.creator = "Inspiration Publication";
        workbook.created = new Date();

        const visitSheet = workbook.addWorksheet("Visits");

        visitSheet.columns = [
            { header: "Visit ID", key: "id", width: 12 },
            { header: "Visit Date", key: "date", width: 16 },
            { header: "Visit Type", key: "type", width: 15 },
            { header: "School Name", key: "school", width: 32 },
            { header: "School Code", key: "schoolCode", width: 15 },
            { header: "Party Name", key: "party", width: 25 },
            { header: "Person Met", key: "personMet", width: 25 },
            { header: "Designation", key: "designation", width: 20 },
            { header: "Contact Number", key: "contactNumber", width: 18 },
            { header: "Purpose", key: "purpose", width: 30 },
            { header: "Assessment", key: "assessment", width: 30 },
            { header: "Follow-up / Next Action", key: "followUp", width: 35 },
            { header: "Remarks", key: "remarks", width: 35 },
            { header: "Start Time", key: "startAt", width: 20 },
            { header: "End Time", key: "endAt", width: 20 },
            { header: "Total KM", key: "totalKm", width: 12 },
            { header: "Location", key: "location", width: 35 },
            { header: "Address", key: "address", width: 35 },
            { header: "Expense Type", key: "expenseTypes", width: 30 },
            { header: "Claimed Expense", key: "claimed", width: 25 },
            { header: "Approved Expense", key: "approved", width: 25 },
            { header: "Total Amount", key: "totalAmount", width: 18 },
            { header: "Status", key: "status", width: 18 },
        ];

        visits.forEach((visit) => {
    const visitExpenses = expenses.filter(
        (expense) =>
            String(expense?.visitId ?? "") ===
            String(visit?.id ?? "")
    );

    const expenseTypes = visitExpenses
        .map((expense) =>
            formatStatus(expense?.expenseType ?? "")
        )
        .filter(Boolean)
        .join(", ");

    const claimedAmounts = visitExpenses.map((expense) =>
        Number(expense?.claimedAmount ?? 0)
    );

    const approvedAmounts = visitExpenses.map((expense) =>
        Number(expense?.approvedAmount ?? 0)
    );

    const totalClaimed = claimedAmounts.reduce(
        (total, amount) => total + amount,
        0
    );

    const statuses = visitExpenses.map((expense) =>
        String(expense?.status ?? "")
            .trim()
            .toLowerCase()
            .replace(/[\s-]+/g, "_")
    );

    let expenseStatus = formatStatus(visit?.status ?? "Pending");

    if (visitExpenses.length > 0) {
        if (
            statuses.some((status) =>
                ["rejected", "reject", "declined"].includes(status)
            )
        ) {
            expenseStatus = "Rejected";
        } else if (
            statuses.every((status) => status === "approved")
        ) {
            expenseStatus = "Approved";
        } else {
            expenseStatus = "Pending";
        }
    }

    visitSheet.addRow({
        id: visit.id,
        date: formatDate(visit.visitDate),
        type: formatStatus(visit.visitType),
        school: visit.school?.name ?? "",
        schoolCode: visit.school?.schoolCode ?? "",
        party: visit.party?.name ?? visit.partyName ?? "",
        personMet:
            visit.personMet ??
            visit.peopleMet?.[0]?.name ??
            "",
        designation:
            visit.designation ??
            visit.peopleMet?.[0]?.designation ??
            "",
        contactNumber:
            visit.contactNumber ??
            visit.peopleMet?.[0]?.phone ??
            "",
        purpose: visit.purpose ?? "",
        assessment: visit.assessment ?? "",
        followUp: visit.followUp ?? visit.followups?.[0]?.note ?? "",
        remarks: visit.remarks ?? "",
        startAt: formatTime(visit.startAt),
        endAt: formatTime(visit.endAt),
        totalKm: Number(visit.totalKm ?? 0),
        location:
            visit.latitude != null && visit.longitude != null
                ? `https://www.google.com/maps?q=${visit.latitude},${visit.longitude}`
                : "",
        address: visit.address ?? "",

        // All expenses linked to this visit
        expenseTypes:
            expenseTypes || (visitExpenses.length === 0 ? "" : "-"),

        claimed:
            visitExpenses.length > 0
                ? claimedAmounts.join(", ")
                : Number(visit.claimedExpense ?? 0),

        approved:
            visitExpenses.length > 0
                ? approvedAmounts.join(", ")
                : Number(visit.approvedExpense ?? 0),

        totalAmount:
            visitExpenses.length > 0
                ? totalClaimed
                : Number(visit.claimedExpense ?? 0),

        status: expenseStatus,
    });
});
        const expenseSheet = workbook.addWorksheet("Expenses");

        expenseSheet.columns = [
            { header: "Expense ID", key: "id", width: 14 },
            { header: "Date", key: "date", width: 16 },
            { header: "Expense Type", key: "type", width: 18 },
            { header: "Title", key: "title", width: 30 },
            { header: "Description", key: "description", width: 30 },
            { header: "Visit ID", key: "visitId", width: 12 },
            { header: "From", key: "from", width: 35 },
            { header: "To", key: "to", width: 35 },
            { header: "Distance KM", key: "distance", width: 15 },
            { header: "Travel By", key: "travelBy", width: 18 },
            { header: "Claimed Amount", key: "claimed", width: 20 },
            { header: "Approved Amount", key: "approved", width: 20 },
            { header: "Status", key: "status", width: 18 },
            { header: "Team Leader Remark", key: "teamLeaderRemark", width: 30 },
            { header: "Admin Remark", key: "adminRemark", width: 30 },
            { header: "Approval Role", key: "approvedByRole", width: 20 },
            { header: "Remarks", key: "remarks", width: 30 },
            { header: "Approved By", key: "approvedBy", width: 25 },
        ];

        expenses.forEach((expense) => {
            expenseSheet.addRow({
                id: expense.id,
                date: formatDate(expense.expenseDate),
                type: formatStatus(expense.expenseType),
                title: expense.title ?? "",
                description: expense.description ?? "",
                visitId: expense.visitId ?? "",
                from: expense.fromLocation ?? "",
                to: expense.toLocation ?? "",
                distance: Number(expense.distanceKm ?? 0),
                travelBy: formatStatus(expense.travelBy),
                claimed: Number(expense.claimedAmount ?? 0),
                approved: Number(expense.approvedAmount ?? 0),
                status: formatStatus(expense.status),
                teamLeaderRemark: expense.teamLeaderRemark ?? "",
                adminRemark: expense.adminRemark ?? "",
                approvedByRole: expense.approvedByRole ?? "",
                remarks: expense.remarks ?? "",
                approvedBy:
                    typeof expense.approvedBy === "object"
                        ? expense.approvedBy?.name ?? ""
                        : expense.approvedBy ?? "",
            });
        });

        // Format headers
        [visitSheet, expenseSheet].forEach((sheet) => {
            sheet.getRow(1).font = { bold: true };
            sheet.getRow(1).alignment = {
                vertical: "middle",
                wrapText: true,
            };
            sheet.views = [{ state: "frozen", ySplit: 1 }];
            sheet.autoFilter = {
                from: "A1",
                to: `${sheet.getColumn(sheet.columnCount).letter}1`,
            };
        });

        const buffer = await workbook.xlsx.writeBuffer();

        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download =
            `daily_working_${employee.employeeCode ?? employee.id}_${details.date}.xlsx`;

        document.body.appendChild(link);
        link.click();
        link.remove();

        URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Daily working export failed:", error);

        showError(
            error?.message || "Unable to export daily working details."
        );
    }
};


    // Print a separate, reference-style expense report without changing Excel export or APIs.
    const exportExpensePdf = () => {
        if (!details || typeof details !== "object") {
            showError("Please open a Daily Working Report before exporting PDF.");
            return;
        }

        const escapeHtml = (value) =>
            String(value ?? "")
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;");

        const money = (value) => {
            const amount = Number(value ?? 0);
            return Number.isFinite(amount)
                ? amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : "0.00";
        };

        const expenseTypeKey = (expense) =>
            String(expense?.expenseType ?? "").toLowerCase().replace(/[^a-z]/g, "");

        const categories = [
            { label: "TRAVELLING", match: (key) => /travel|travelling|transport/.test(key) },
            { label: "HALF/FULL", match: (key) => /da|dailyallowance|fixedallowance|halfday/.test(key) },
            { label: "LOCAL CONVEYANCE/AUTO/TAXI", match: (key) => /local|conveyance|auto|taxi|kilometer|km/.test(key) },
            { label: "SUNDRY EXPENSE WITH DETAIL", match: (key) => /sundry|miscellaneous|other/.test(key) },
            { label: "MOBILE EXPENSES", match: (key) => /mobile|phone/.test(key) },
        ];

        const allExpenses = Array.isArray(details.expenses) ? details.expenses : [];
        const categoryAmounts = categories.map((category) => {
            const matching = allExpenses.filter((expense) => category.match(expenseTypeKey(expense)));
            return {
                ...category,
                amount: matching.reduce((sum, expense) => sum + Number(expense?.claimedAmount ?? 0), 0),
                details: matching.map((expense) => expense?.remarks || expense?.description || expense?.title || "").filter(Boolean).join("; "),
            };
        });

        // If an application's expense type does not match the known categories,
        // keep its value visible in the sundry line instead of dropping any amount.
        const recognized = allExpenses.filter((expense) => categories.some((category) => category.match(expenseTypeKey(expense))));
        const unclassified = allExpenses.filter((expense) => !recognized.includes(expense));
        if (unclassified.length) {
            const sundry = categoryAmounts.find((item) => item.label === "SUNDRY EXPENSE WITH DETAIL");
            sundry.amount += unclassified.reduce((sum, expense) => sum + Number(expense?.claimedAmount ?? 0), 0);
            sundry.details = [sundry.details, ...unclassified.map((expense) => expense?.remarks || expense?.description || expense?.title || "").filter(Boolean)].filter(Boolean).join("; ");
        }

        const total = allExpenses.reduce((sum, expense) => sum + Number(expense?.claimedAmount ?? 0), 0);
        const visits = Array.isArray(details.visits) ? details.visits : [];
        const employee = details.employee || {};
        const travelBy = allExpenses.map((expense) => expense?.travelBy).filter(Boolean).map((value) => String(value).replace(/_/g, " "));
        const uniqueTravelBy = [...new Set(travelBy)];
        const dateLabel = formatDate(details.date || allExpenses[0]?.expenseDate || visits[0]?.visitDate);

        const expenseRows = categoryAmounts.map((item) => `
            <tr>
                <td>${escapeHtml(item.label)}</td>
                <td class="currency">RS</td>
                <td class="amount">${money(item.amount)}</td>
                <td>${escapeHtml(item.details)}</td>
            </tr>
        `).join("");

        const visitRows = visits.map((visit) => `
            <tr>
                <td>${escapeHtml(formatDate(visit?.visitDate || details.date))}</td>
                <td>${escapeHtml(visit?.visitType || "SCHOOL VISIT")}</td>
                <td>${escapeHtml(visit?.school?.name || visit?.party?.name || visit?.partyName || "")}</td>
                <td>${escapeHtml(visit?.school?.city?.name || visit?.city?.name || "")}</td>
                <td>${escapeHtml(visit?.purpose || "")}</td>
                <td>${escapeHtml(visit?.assessment || visit?.remarks || "")}</td>
            </tr>
        `).join("");

        const popup = window.open("", "_blank", "width=1000,height=800");
        if (!popup) {
            showError("Please allow pop-ups to print or save the expense report as PDF.");
            return;
        }

        popup.document.open();
        popup.document.write(`<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Expense Report - ${escapeHtml(employee.name || employee.fullName || "Employee")}</title>
<style>
    @page { size: A4 portrait; margin: 10mm 12mm 12mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; color: #222; font-size: 9px; }
    .company-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid #777;
        padding-bottom: 8px;
        min-height: 65px;
    }

    .logo-container {
        width: 48%;
        display: flex;
        align-items: center;
    }

    .company-logo {
        max-width: 260px;
        width: 100%;
        height: 65px;
        object-fit: contain;
        object-position: left center;
    }

    .company-name {
        font-size: 16px;
        font-weight: bold;
        text-align: right;
    }
    .company { display: grid; grid-template-columns: 42% 58%; align-items: center; min-height: 36mm; padding: 0 2mm 2mm; }
    /* Reserved logo area: intentionally blank until the company logo is supplied. */
    .logo-space { height: 31mm; width: 100%; }
    .company-name { font-size: 16px; font-weight: 700; text-align: right; white-space: nowrap; }
    h1 { text-align: center; font-size: 15px; margin: 0 0 7px; line-height: 1.2; }
    .meta { border: 1px solid #444; padding: 8px 8px 5px; margin-bottom: 4px; }
    .meta-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 8px; min-height: 27px; align-items: center; }
    .meta-grid > div { white-space: nowrap; }
    table { border-collapse: collapse; width: 100%; table-layout: fixed; }
    th, td { border: 1px solid #555; padding: 4px 5px; overflow-wrap: anywhere; vertical-align: top; }
    .expense-table { margin: 3px auto 3px; width: 92%; }
    .expense-table col:nth-child(1) { width: 36%; }
    .expense-table col:nth-child(2) { width: 7%; }
    .expense-table col:nth-child(3) { width: 25%; }
    .expense-table col:nth-child(4) { width: 32%; }
    .expense-table .currency { text-align: center; }
    .expense-table .amount { text-align: right; font-weight: 600; }
    .total td { font-weight: 700; }
    .travel { padding: 4px 10px 2px; line-height: 1.6; }
    .visits-title { display: none; }
    .visit-table { margin-top: 3px; }
    .visit-table th { background: #eee; text-align: left; font-weight: 400; }
    .visit-table col:nth-child(1) { width: 12%; }
    .visit-table col:nth-child(2) { width: 14%; }
    .visit-table col:nth-child(3) { width: 25%; }
    .visit-table col:nth-child(4) { width: 9%; }
    .visit-table col:nth-child(5) { width: 16%; }
    .visit-table col:nth-child(6) { width: 24%; }
    .office { display: grid; grid-template-columns: 40% 60%; margin-top: 38mm; min-height: 27mm; page-break-inside: avoid; }
    .office > div { border: 1px solid #555; padding: 7px; }
    .office-right { line-height: 2.5; }
    .footer { display: flex; justify-content: space-between; margin-top: 5px; font-size: 8px; }
    .print-button { margin-top: 20px; text-align: center; }
    @media print { .no-print { display: none !important; } tr { page-break-inside: avoid; } }
</style>
</head>
<body>
    <header class="company">
        <div class="logo-space" aria-label="Reserved space for company logo">
            <img
                src="https://crm.edutools.in/inspiration-logo.jfif"
                alt="Inspiration Publication Logo"
                class="company-logo"
            />
        </div>
        <div class="company-name">INSPIRATION PUBLICATION (INDIA)</div>
    </header>
    <h1>EXPENSE REPORT</h1>
    <section class="meta">
        <div class="meta-grid">
            <div><strong>SALESMAN NAME</strong> &nbsp;&nbsp; ${escapeHtml(employee.name || employee.fullName || employee.employeeName || "")}</div>
            <div><strong>DATE</strong> &nbsp;&nbsp; ${escapeHtml(dateLabel)}</div>
        </div>
        <table class="expense-table">
            <colgroup><col /><col /><col /><col /></colgroup>
            <tbody>
                ${expenseRows}
                <tr class="total"><td>TOTAL</td><td class="currency">RS</td><td class="amount">${money(total)}</td><td></td></tr>
            </tbody>
        </table>
        <div class="travel"><strong>TRAVEL BY</strong> &nbsp;&nbsp; ${escapeHtml(uniqueTravelBy.join(", "))}<br /><strong>REMARK</strong> &nbsp;&nbsp; ${escapeHtml(allExpenses.map((expense) => expense?.remarks).filter(Boolean).join("; "))}</div>
    </section>
    <table class="visit-table">
        <colgroup><col /><col /><col /><col /><col /><col /></colgroup>
        <thead><tr><th>Date</th><th>Type</th><th>School / Party</th><th>City</th><th>Visited For</th><th>Assessment of Visit</th></tr></thead>
        <tbody>${visitRows || '<tr><td colspan="6">&nbsp;</td></tr>'}</tbody>
    </table>
    <section class="office">
        <div></div>
        <div class="office-right"><strong>FOR OFFICE USE ONLY</strong><br />CHECKED BY: __________________________<br />REMARKS FOR WORKING: __________________________</div>
    </section>
    <div class="footer"><span>${escapeHtml(new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }))}</span><span>Page 1 of 1</span></div>
    <div class="no-print print-button"><button onclick="window.print()">Print / Save as PDF</button></div>
    <script>
        // Preview only: do not automatically invoke the browser print dialog.
        // The visible Print / Save as PDF button remains available in the new tab.
    </script>
</body>
</html>`);
        popup.document.close();
        // Open the PDF preview in a new tab without triggering the print dialog automatically.
        // The user can choose Print / Save as PDF from the preview when ready.
    };


    return (
        <div className="space-y-5">
            {/* Header */}
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-navy-700 dark:bg-navy-800">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <div className="flex flex-wrap items-center gap-3">
                            <h2 className="text-xl font-bold text-navy-700 dark:text-white">
                                Daily Working Report
                            </h2>

                            <span
                                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                                    approved
                                        ? "bg-green-100 text-green-700"
                                        : "bg-red-100 text-red-700"
                                }`}
                            >
                                {approved
                                    ? "Approved"
                                    : "Pending"}
                            </span>
                        </div>

                        <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-500">
                            <span>
                                <strong>
                                    Employee:
                                </strong>{" "}
                                {displayValue(
                                    employee?.name
                                )}
                            </span>

                            <span>
                                <strong>
                                    Employee Code:
                                </strong>{" "}
                                {displayValue(
                                    employee?.employeeCode
                                )}
                            </span>

                            <span>
                                <strong>
                                    Date:
                                </strong>{" "}
                                {formatDate(
                                    details?.date
                                )}
                            </span>
                        </div>
                    </div>

                     <button
        type="button"
        onClick={exportDailyDetailsExcel}
        className="h-10 rounded-lg bg-green-600 px-4 text-sm font-semibold text-white hover:bg-green-700"
    >
        Export Excel
    </button>
    <button
        type="button"
        onClick={exportExpensePdf}
        className="h-10 rounded-lg bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700"
    >
        Export PDF
    </button>

                    <button
                        type="button"
                        onClick={onBack}
                        className="h-10 rounded-lg border border-gray-300 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-navy-600 dark:text-white"
                    >
                        ← Back to Reports
                    </button>
                </div>
            </section>

            {/* Summary */}
            <section className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-navy-700 dark:bg-navy-800">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Total Visits
                    </p>

                    <p className="mt-2 text-2xl font-bold text-navy-700 dark:text-white">
                        {Number(
                            summary?.totalVisits ??
                                visits.length
                        )}
                    </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-navy-700 dark:bg-navy-800">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Total Expenses
                    </p>

                    <p className="mt-2 text-2xl font-bold text-navy-700 dark:text-white">
                        {Number(
                            summary?.expenseCount ??
                                expenses.length
                        )}
                    </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-navy-700 dark:bg-navy-800">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Claimed Expense
                    </p>

                    <p className="mt-2 text-2xl font-bold text-navy-700 dark:text-white">
                        {formatAmount(
                            summary?.claimedExpense
                        )}
                    </p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-navy-700 dark:bg-navy-800">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Approved Expense
                    </p>

                    <p className="mt-2 text-2xl font-bold text-navy-700 dark:text-white">
                        {formatAmount(
                            summary?.approvedExpense
                        )}
                    </p>
                </div>
            </section>

            {/* Approval information */}
            <section
                className={`rounded-xl border p-4 ${
                    approved
                        ? "border-green-200 bg-green-50"
                        : "border-red-200 bg-red-50"
                }`}
            >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p
                            className={`text-sm font-bold ${
                                approved
                                    ? "text-green-800"
                                    : "text-red-800"
                            }`}
                        >
                            Expense Approval:{" "}
                            {approved
                                ? "Approved"
                                : "Pending / Not Fully Approved"}
                        </p>

                        <p
                            className={`mt-1 text-xs ${
                                approved
                                    ? "text-green-700"
                                    : "text-red-700"
                            }`}
                        >
                            {approved
                                ? "All expenses for this employee and date have been approved."
                                : "At least one expense is not approved yet."}
                        </p>
                    </div>

                    {approval?.approvedBy && (
                        <div className="text-xs text-gray-600">
                            Approved By:{" "}
                            {displayValue(
                                approval.approvedBy
                            )}
                        </div>
                    )}
                </div>
            </section>

            {/* Visits */}
            <section className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-navy-700 dark:bg-navy-800">
                <div className="border-b border-gray-200 px-5 py-4 dark:border-navy-700">
                    <h3 className="text-base font-bold text-navy-700 dark:text-white">
                        Visits
                    </h3>

                    <p className="mt-1 text-xs text-gray-500">
                        All visits recorded for this employee on this date.
                    </p>
                </div>

                <SimpleTable
                    columns={
                        DAILY_VISIT_COLUMNS
                    }
                    rows={visits}
                    emptyText="No visits found for this date."
                />
            </section>

            {/* Expenses */}
            <section className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-navy-700 dark:bg-navy-800">
                <div className="border-b border-gray-200 px-5 py-4 dark:border-navy-700">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h3 className="text-base font-bold text-navy-700 dark:text-white">
                                Expenses
                            </h3>
                            <p className="mt-1 text-xs text-gray-500">
                                All expenses recorded for this employee on this date.
                            </p>
                        </div>

                        {canEditExpenses && pendingExpenses.length > 0 && (
                            <button
                                type="button"
                                disabled={bulkApproving}
                                onClick={() => onApproveAllExpenses?.(pendingExpenses)}
                                className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-green-600 px-4 text-sm font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {bulkApproving ? "Approving..." : `Approve All (${pendingExpenses.length})`}
                            </button>
                        )}
                    </div>
                </div>

                <SimpleTable
                    columns={DAILY_EXPENSE_COLUMNS}
                    rows={expenses}
                    emptyText="No expenses found for this date."
                    canEditExpenses={canEditExpenses}
                    onEditExpense={onEditExpense}
                    onApproveExpense={onApproveExpense}
                />
            </section>
        </div>
    );
}


/*
|--------------------------------------------------------------------------
| MAIN REPORTS COMPONENT
|--------------------------------------------------------------------------
*/

export default function Reports() {
    const [reportType, setReportType] =
        useState("daily_working");

    const [filters, setFilters] =
        useState({
            ...DEFAULT_FILTERS,
        });

    const [records, setRecords] =
        useState([]);

    const [summary, setSummary] =
        useState({});

    const [loading, setLoading] =
        useState(false);

    const [hasGenerated, setHasGenerated] =
        useState(false);

    const [employees, setEmployees] =
        useState([]);

    const [journeys, setJourneys] =
        useState([]);

    const [visits, setVisits] =
        useState([]);

    const [employeeLoading, setEmployeeLoading] =
        useState(false);

    const [journeyLoading, setJourneyLoading] =
        useState(false);

    const [visitLoading, setVisitLoading] =
        useState(false);

    const [statementOpen, setStatementOpen] =
        useState(false);

    const [statementRefreshKey, setStatementRefreshKey] =
        useState(0);

    /*
    |--------------------------------------------------------------------------
    | DAILY DETAILS
    |--------------------------------------------------------------------------
    */

    const [dailyWorkingDetails, setDailyWorkingDetails] =
        useState(null);

    const [dailyWorkingDetailsLoading, setDailyWorkingDetailsLoading] =
        useState(false);

    const [editingExpense, setEditingExpense] = useState(null);
    const [expenseEditOpen, setExpenseEditOpen] = useState(false);
    const [bulkApproving, setBulkApproving] = useState(false);

    const currentUser = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("user") || "{}");
        } catch {
            return {};
        }
    }, []);

    // Support the user shapes used by the existing auth flow without
    // changing any report filters, columns, or data-loading behaviour.
    const roleValues = [
        ...(Array.isArray(currentUser?.roles) ? currentUser.roles : []),
        ...(currentUser?.role ? [currentUser.role] : []),
        ...(currentUser?.user?.role ? [currentUser.user.role] : []),
    ];

    const currentRoles = roleValues
        .map((role) => {
            if (typeof role === "string") return role;
            return role?.slug ?? role?.name ?? role?.role ?? "";
        })
        .map((role) => String(role).trim().toLowerCase().replace(/[ -]+/g, "_"));

    const canAdminEditExpense =
        currentRoles.includes("admin") ||
        currentRoles.includes("super_admin") ||
        currentRoles.includes("superadmin");


    const selectedReport = useMemo(
        () =>
            REPORTS.find(
                (item) =>
                    item.key === reportType
            ),
        [reportType]
    );


    /*
    |--------------------------------------------------------------------------
    | LOAD EMPLOYEES
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        let active = true;

        async function loadEmployees() {
            setEmployeeLoading(true);

            try {
                const response =
                    await getEmployees(
                        1,
                        100
                    );

                if (active) {
                    setEmployees(
                        getList(
                            response,
                            [
                                "employees",
                                "records",
                                "items",
                            ]
                        )
                    );
                }
            } catch (error) {
                console.error(
                    "Unable to load employees:",
                    error
                );

                if (active) {
                    setEmployees([]);
                }
            } finally {
                if (active) {
                    setEmployeeLoading(false);
                }
            }
        }

        loadEmployees();

        return () => {
            active = false;
        };
    }, []);


    /*
    |--------------------------------------------------------------------------
    | LOAD JOURNEYS
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        let active = true;

        setJourneys([]);
        setVisits([]);

        if (!filters.employeeId) {
            return () => {
                active = false;
            };
        }

        async function loadJourneys() {
            setJourneyLoading(true);

            try {
                const response =
                    await getJourneyPlans(
                        1,
                        100,
                        {
                            employeeId:
                                filters.employeeId,
                        }
                    );

                if (!active) {
                    return;
                }

                const plans =
                    getList(
                        response,
                        [
                            "journeyPlans",
                            "journeys",
                            "plans",
                            "records",
                            "items",
                        ]
                    );

                setJourneys(
                    getJourneyItems(
                        plans
                    )
                );
            } catch (error) {
                console.error(
                    "Unable to load journeys:",
                    error
                );

                if (active) {
                    setJourneys([]);
                }
            } finally {
                if (active) {
                    setJourneyLoading(false);
                }
            }
        }

        loadJourneys();

        return () => {
            active = false;
        };
    }, [filters.employeeId]);


    /*
    |--------------------------------------------------------------------------
    | LOAD VISITS FOR JOURNEY FILTER
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        let active = true;

        setVisits([]);

        if (
            !filters.employeeId ||
            !filters.journeyPlanItemId
        ) {
            return () => {
                active = false;
            };
        }

        async function loadVisits() {
            setVisitLoading(true);

            try {
                const response =
                    await getVisits(
                        1,
                        100,
                        {
                            employeeId:
                                filters.employeeId,

                            journeyPlanItemId:
                                filters.journeyPlanItemId,
                        }
                    );

                if (!active) {
                    return;
                }

                setVisits(
                    getList(
                        response,
                        [
                            "visits",
                            "records",
                            "items",
                        ]
                    )
                );
            } catch (error) {
                console.error(
                    "Unable to load visits:",
                    error
                );

                if (active) {
                    setVisits([]);
                }
            } finally {
                if (active) {
                    setVisitLoading(false);
                }
            }
        }

        loadVisits();

        return () => {
            active = false;
        };
    }, [
        filters.employeeId,
        filters.journeyPlanItemId,
    ]);


    /*
    |--------------------------------------------------------------------------
    | FILTER HANDLERS
    |--------------------------------------------------------------------------
    */

    const updateFilter = (
        name,
        value
    ) => {
        setFilters(
            (previous) => ({
                ...previous,
                [name]: value,
            })
        );
    };


    const handleEmployeeChange = (
        employeeId
    ) => {
        setFilters(
            (previous) => ({
                ...previous,
                employeeId,
                journeyPlanItemId: "",
                visitId: "",
            })
        );

        setJourneys([]);
        setVisits([]);
        setRecords([]);
        setSummary({});
        setHasGenerated(false);
        setDailyWorkingDetails(null);
    };


    const handleJourneyChange = (
        journeyPlanItemId
    ) => {
        setFilters(
            (previous) => ({
                ...previous,
                journeyPlanItemId,
                visitId: "",
            })
        );

        setVisits([]);
        setRecords([]);
        setSummary({});
        setHasGenerated(false);
    };


    const resetFilters = () => {
        setFilters({
            ...DEFAULT_FILTERS,
        });

        setJourneys([]);
        setVisits([]);
        setRecords([]);
        setSummary({});
        setHasGenerated(false);
        setDailyWorkingDetails(null);
    };


    const changeReportType = (
        value
    ) => {
        setReportType(value);

        setFilters({
            ...DEFAULT_FILTERS,
        });

        setJourneys([]);
        setVisits([]);
        setRecords([]);
        setSummary({});
        setHasGenerated(false);
        setDailyWorkingDetails(null);
    };


    /*
    |--------------------------------------------------------------------------
    | LOAD REPORT
    |--------------------------------------------------------------------------
    */

    async function loadReport() {
        if (
            filters.fromDate &&
            filters.toDate &&
            filters.fromDate >
                filters.toDate
        ) {
            showError(
                "From Date cannot be after To Date."
            );

            return;
        }

        try {
            setLoading(true);

            const cleanFilters =
                Object.fromEntries(
                    Object.entries(
                        filters
                    ).filter(
                        ([, value]) =>
                            value !== "" &&
                            value !== null &&
                            value !==
                                undefined
                    )
                );

            /*
            |--------------------------------------------------------------------------
            | DAILY WORKING
            |--------------------------------------------------------------------------
            */

            if (
                reportType ===
                "daily_working"
            ) {
                /*
                | Keep server result within existing
                | maximum page size.
                */
                const response =
                    await getDailyWorkingSummary(
                        {
                            ...cleanFilters,
                            page: 1,
                            limit: 100,
                        }
                    );

                const root =
                    response?.data ??
                    response;

                const payload =
                    root?.data ??
                    root;

                const nextRecords =
                    Array.isArray(
                        payload?.records
                    )
                        ? payload.records
                        : [];

                setRecords(
                    nextRecords
                );

                setSummary(
                    payload?.summary ??
                        {}
                );

                setHasGenerated(
                    true
                );

                return;
            }


            /*
            |--------------------------------------------------------------------------
            | OTHER REPORTS
            |--------------------------------------------------------------------------
            */

            const apiFunction =
                REPORT_FUNCTIONS[
                    reportType
                ];

            if (
                typeof apiFunction !==
                "function"
            ) {
                showError(
                    "Report API is not configured."
                );

                return;
            }

            const response =
                await apiFunction(
                    cleanFilters
                );

            const root =
                response?.data ??
                response;

            const payload =
                root?.data ??
                root;

            const nextRecords =
                Array.isArray(
                    payload?.records
                )
                    ? payload.records
                    : Array.isArray(
                          payload?.items
                      )
                        ? payload.items
                        : Array.isArray(
                              payload?.data
                          )
                            ? payload.data
                            : Array.isArray(
                                  root
                              )
                              ? root
                              : [];

            setRecords(
                nextRecords
            );

            setSummary(
                payload?.summary ??
                    {}
            );

            setHasGenerated(true);
        } catch (error) {
            console.error(
                "Report loading error:",
                error
            );

            setRecords([]);
            setSummary({});
            setHasGenerated(false);

            showError(
                error?.response?.data
                    ?.message ||
                    error?.message ||
                    "Unable to load report."
            );
        } finally {
            setLoading(false);
        }
    }


    /*
    |--------------------------------------------------------------------------
    | DAILY WORKING DETAILS
    |--------------------------------------------------------------------------
    */

    async function handleDailyWorkingView(row) {
    console.log(
        "Daily Working View Row:",
        row
    );

    const employeeId =
        row?.employeeId ??
        row?.employee?.id;

    const date = row?.date;

    if (!employeeId || !date) {
        showError(
            "Employee ID or date is missing."
        );

        console.error(
            "Invalid Daily Working row:",
            row
        );

        return;
    }

    try {
        setDailyWorkingDetailsLoading(
            true
        );

        console.log(
            "Loading Daily Working Details:",
            {
                employeeId,
                date,
            }
        );

        const response =
            await getDailyWorkingDetails(
                employeeId,
                date
            );

        console.log(
            "Daily Working Details API Response:",
            response
        );

        /*
        |--------------------------------------------------------------------------
        | Normalize API response
        |--------------------------------------------------------------------------
        */

        // Axios may return { data: { data: details } } or { data: details }.
        // Normalize both shapes without changing the existing report APIs.
        const root = response?.data ?? response;
        const payload = root?.data ?? root;

        if (!payload || typeof payload !== "object") {
            throw new Error(
                "Daily working details response is empty."
            );
        }

        setDailyWorkingDetails(payload);
    } catch (error) {
        console.error(
            "Daily working details error:",
            error
        );

        console.error(
            "API error response:",
            error?.response?.data
        );

        showError(
            error?.response?.data?.message ||
                error?.message ||
                "Unable to load daily working details."
        );
    } finally {
        setDailyWorkingDetailsLoading(
            false
        );
    }
}


async function exportExcel() {
    if (!Array.isArray(records) || records.length === 0) {
        showError("Please generate the Daily Working Report before exporting.");
        return;
    }

    try {
        setLoading(true);

        const workbook = new ExcelJS.Workbook();
        workbook.creator = "Daily Working Report";
        workbook.createdAt = new Date();

        const greenFill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFC6EFCE" },
        };

        const redFill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFFC7CE" },
        };

        const headerFill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF1F4E78" },
        };

        const getPayload = (response) =>
            response?.data?.data ??
            response?.data ??
            response;

        const getName = (value) =>
            value?.name ?? value?.title ?? "";

        const formatRows = (sheet, rows) => {
            sheet.columns = rows.length
                ? Object.keys(rows[0]).map((key) => ({
                    header: key,
                    key,
                    width: Math.min(
                        Math.max(key.length + 5, 16),
                        35
                    ),
                }))
                : [{ header: "No Data", key: "empty", width: 20 }];

            sheet.getRow(1).eachCell((cell) => {
                cell.font = {
                    bold: true,
                    color: { argb: "FFFFFFFF" },
                };
                cell.fill = headerFill;
                cell.alignment = { vertical: "middle" };
            });

            rows.forEach((item) => {
                const row = sheet.addRow(item);
                const isApproved = item.ApprovalStatus === "Approved";

                row.eachCell((cell) => {
                    cell.fill = isApproved ? greenFill : redFill;
                    cell.alignment = {
                        vertical: "top",
                        wrapText: true,
                    };
                });
            });

            sheet.views = [{ state: "frozen", ySplit: 1 }];
            sheet.autoFilter = {
                from: "A1",
                to: `${sheet.getColumn(sheet.columnCount).letter}1`,
            };
        };

        const summaryRows = [];
        const journeyRows = [];
        const visitRows = [];
        const expenseRows = [];

        for (const record of records) {
            const employeeId =
                record?.employeeId ?? record?.employee?.id;
            const date = record?.date;

            if (!employeeId || !date) continue;

            const response = await getDailyWorkingDetails(
                employeeId,
                date
            );

            const details = getPayload(response);
            const isApproved =
                details?.approval?.allExpensesApproved === true ||
                String(details?.approval?.status ?? record?.approvalStatus ?? "").toLowerCase() === "approved";

            const approvalStatus = isApproved
                ? "Approved"
                : "Pending";

            const employeeName =
                details?.employee?.name ??
                record?.employee?.name ??
                "";

            const employeeCode =
                details?.employee?.employeeCode ??
                record?.employee?.employeeCode ??
                "";

            const common = {
                Employee: employeeName,
                EmployeeCode: employeeCode,
                Date: date,
                ApprovalStatus: approvalStatus,
            };

            summaryRows.push({
                ...common,
                TotalVisits: details?.summary?.totalVisits ?? 0,
                TotalExpenses: details?.summary?.totalExpenses ?? 0,
                ClaimedExpense: details?.summary?.claimedExpense ?? 0,
                ApprovedExpense: details?.summary?.approvedExpense ?? 0,
                TotalKM: details?.summary?.totalKm ?? 0,
            });

            (Array.isArray(details?.visits) ? details.visits : []).forEach((visit) => {
                const journey = visit?.journey ?? {};

                const item = {
                    ...common,
                    VisitId: visit?.id ?? "",
                    VisitDate: visit?.visitDate ?? "",
                    VisitNumber: visit?.visitNumber ?? "",
                    VisitType: visit?.visitType ?? "",
                    SchoolName: visit?.school?.name ?? "",
                    SchoolCode: visit?.school?.schoolCode ?? "",
                    PartyName: visit?.party?.name ?? visit?.partyName ?? "",
                    JourneyId: journey?.id ?? "",
                    JourneyName: journey?.name ?? "",
                    Purpose: visit?.purpose ?? "",
                    Assessment: visit?.assessment ?? "",
                    Remarks: visit?.remarks ?? "",
                    StartTime: visit?.startAt ?? "",
                    EndTime: visit?.endAt ?? "",
                    TotalKM: visit?.totalKm ?? 0,
                    Address: visit?.address ?? "",
                    Latitude: visit?.latitude ?? "",
                    Longitude: visit?.longitude ?? "",
                    ClaimedExpense: visit?.claimedExpense ?? 0,
                    ApprovedExpense: visit?.approvedExpense ?? 0,
                };

                visitRows.push(item);

                journeyRows.push({
                    ...common,
                    JourneyId: journey?.id ?? "",
                    JourneyName: journey?.name ?? "",
                    VisitId: visit?.id ?? "",
                    VisitDate: visit?.visitDate ?? "",
                    VisitType: visit?.visitType ?? "",
                    SchoolName: visit?.school?.name ?? "",
                    PartyName: visit?.party?.name ?? visit?.partyName ?? "",
                    Purpose: visit?.purpose ?? "",
                    VisitStatus: visit?.status ?? "",
                });
            });

            (Array.isArray(details?.expenses) ? details.expenses : []).forEach((expense) => {
                expenseRows.push({
                    ...common,
                    ExpenseId: expense?.id ?? "",
                    ExpenseDate: expense?.expenseDate ?? "",
                    ExpenseType: expense?.expenseType ?? "",
                    Title: expense?.title ?? "",
                    Description: expense?.description ?? "",
                    VisitId: expense?.visitId ?? "",
                    AttendanceId: expense?.attendanceId ?? "",
                    FromLocation: expense?.fromLocation ?? "",
                    ToLocation: expense?.toLocation ?? "",
                    DistanceKM: expense?.distanceKm ?? 0,
                    TravelBy: expense?.travelBy ?? "",
                    ClaimedAmount: expense?.claimedAmount ?? 0,
                    ApprovedAmount: expense?.approvedAmount ?? 0,
                    Status: expense?.status ?? "",
                    ApprovedByRole: expense?.approvedByRole ?? "",
                    TeamLeaderRemark: expense?.teamLeaderRemark ?? "",
                    AdminRemark: expense?.adminRemark ?? "",
                    Remarks: expense?.remarks ?? "",
                    BillNo: expense?.billNo ?? "",
                    BillDate: expense?.billDate ?? "",
                    ReceiptFiles: (expense?.files ?? [])
                        .map((file) => file.filePath)
                        .filter(Boolean)
                        .join("; "),
                    ApprovedBy: getName(expense?.approvedBy),
                });
            });
        }

        formatRows(
            workbook.addWorksheet("Daily Summary"),
            summaryRows
        );

        formatRows(
            workbook.addWorksheet("Journey Details"),
            journeyRows
        );

        formatRows(
            workbook.addWorksheet("Visit Details"),
            visitRows
        );

        formatRows(
            workbook.addWorksheet("Expense Details"),
            expenseRows
        );

        const buffer = await workbook.xlsx.writeBuffer();

        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `daily_working_${new Date()
            .toISOString()
            .slice(0, 10)}.xlsx`;

        document.body.appendChild(link);
        link.click();
        link.remove();

        URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Excel export failed:", error);

        showError(
            error?.response?.data?.message ||
            error?.message ||
            "Unable to export Excel report."
        );
    } finally {
        setLoading(false);
    }
}

    // Expense Report PDF: uses the currently generated expense report data and
    // fetches daily-working details only to include the visits for each employee/date.
    // This is isolated from the existing Excel/CSV exports and report APIs.
    async function exportExpenseReportPdf() {
        if (!Array.isArray(records) || records.length === 0) {
            showError("Please generate the Expense Report before exporting PDF.");
            return;
        }

        const escapeHtml = (value) => String(value ?? "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");

        const amount = (value) => {
            const n = Number(value ?? 0);
            return Number.isFinite(n) ? n.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }) : "0.00";
        };

        const normalize = (value) => String(value ?? "")
            .toLowerCase().replace(/[^a-z]/g, "");

        const expenseTypeForLine = (expense) => {
            const type = normalize(expense?.expenseType);
            const title = normalize(expense?.title);
            const description = normalize(expense?.description);
            const text = `${type} ${title} ${description}`;
            if (text.includes("fixedda") || text.includes("halfda") || text.includes("dailyallowance") || text.includes("allowance")) return "fixedDa";
            if (text.includes("localconveyance") || text.includes("conveyance") || text.includes("auto") || text.includes("taxi") || text.includes("localtravel")) return "local";
            if (text.includes("sundry") || text.includes("miscellaneous") || text.includes("otherexpense")) return "sundry";
            if (text.includes("mobile") || text.includes("internet")) return "mobile";
            if (text.includes("travel") || text.includes("travelling") || text.includes("transport")) return "travelling";
            return "sundry";
        };

        const getPayload = (response) => response?.data?.data ?? response?.data ?? response;
        const groupMap = new Map();
        records.forEach((record) => {
            const employeeId = record?.employeeId ?? record?.employee?.id ?? record?.employee?.employeeId;
            const rawDate = record?.expenseDate ?? record?.date ?? record?.visitDate;
            const dateKey = rawDate ? String(rawDate).slice(0, 10) : "";
            const key = `${employeeId ?? "unknown"}::${dateKey || "unknown-date"}`;
            if (!groupMap.has(key)) groupMap.set(key, { employeeId, date: rawDate, records: [] });
            groupMap.get(key).records.push(record);
        });

        try {
            setLoading(true);
            const reports = [];
            for (const group of groupMap.values()) {
                let details = null;
                if (group.employeeId && group.date) {
                    try {
                        details = getPayload(await getDailyWorkingDetails(group.employeeId, String(group.date).slice(0, 10)));
                    } catch (error) {
                        // PDF remains available from the selected expense rows if daily details are unavailable.
                        console.warn("Could not load visits for expense PDF:", error);
                    }
                }

                const expenses = Array.isArray(details?.expenses) && details.expenses.length
                    ? details.expenses
                    : group.records;
                const employee = details?.employee ?? group.records[0]?.employee ?? {};
                const date = details?.date ?? group.date ?? group.records[0]?.expenseDate;
                const totals = { travelling: 0, fixedDa: 0, local: 0, sundry: 0, mobile: 0 };
                expenses.forEach((expense) => {
                    const line = expenseTypeForLine(expense);
                    totals[line] += Number(expense?.claimedAmount ?? 0) || 0;
                });
                const total = Object.values(totals).reduce((sum, value) => sum + value, 0);
                const visits = Array.isArray(details?.visits) ? details.visits : [];
                const firstExpense = expenses[0] ?? {};
                const travelBy = expenses.map((expense) => expense?.travelBy).filter(Boolean)[0] ?? firstExpense?.travelMode ?? "";
                const remarks = expenses.map((expense) => expense?.remarks ?? expense?.description).filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join("; ");

                reports.push({ employee, date, totals, total, visits, travelBy, remarks, expenses });
            }

            const visitRows = (visits) => visits.length ? visits.map((visit) => {
                const visitDate = visit?.visitDate ? formatDate(visit.visitDate) : "";
                const name = visit?.school?.name ?? visit?.party?.name ?? visit?.partyName ?? "";
                const city = visit?.school?.city?.name ?? visit?.city?.name ?? "";
                const visitedFor = visit?.purpose ?? visit?.visitType ?? "";
                const assessment = visit?.assessment ?? visit?.remarks ?? "";
                return `<tr><td>${escapeHtml(visitDate)}</td><td>${escapeHtml(formatStatus(visit?.visitType ?? "School Visit"))}</td><td>${escapeHtml(name)}</td><td>${escapeHtml(city)}</td><td>${escapeHtml(visitedFor)}</td><td>${escapeHtml(assessment)}</td></tr>`;
            }).join("") : '<tr><td colspan="6" class="empty">No visits recorded for this date.</td></tr>';

            const pages = reports.map((report, index) => `
                <section class="report-page">
                    <header class="company-header">
                        <div class="logo-word">INSPIRATION</div>
                        <div class="company-name">INSPIRATION PUBLICATION (INDIA)</div>
                    </header>
                    <h1>EXPENSE REPORT</h1>
                    <div class="meta-box">
                        <div class="meta-line"><strong>SALESMAN NAME</strong><span>${escapeHtml(getEmployeeName(report.employee) || getEmployeeName(report.expenses[0]?.employee) || "")}</span><strong>DATE</strong><span>${escapeHtml(formatDate(report.date))}</span></div>
                        <table class="expense-table">
                            <tbody>
                                <tr><td>TRAVELLING</td><td class="currency">RS</td><td class="money">${amount(report.totals.travelling)}</td><td></td></tr>
                                <tr><td>FIXED DA/HALF</td><td class="currency">RS</td><td class="money">${amount(report.totals.fixedDa)}</td><td></td></tr>
                                <tr><td>LOCAL CONVEYANCE/AUTO/TAXI</td><td class="currency">RS</td><td class="money">${amount(report.totals.local)}</td><td>${escapeHtml(report.expenses.map((e) => e?.distanceKm ? `TODAY RUNNING KM ${e.distanceKm}` : "").filter(Boolean)[0] ?? "")}</td></tr>
                                <tr><td>SUNDRY EXPENSE WITH DETAIL</td><td class="currency">RS</td><td class="money">${amount(report.totals.sundry)}</td><td>${escapeHtml(report.expenses.filter((e) => expenseTypeForLine(e) === "sundry").map((e) => e?.title ?? e?.description ?? "").filter(Boolean).join("; "))}</td></tr>
                                <tr><td>MOBILE EXPENSES</td><td class="currency">RS</td><td class="money">${amount(report.totals.mobile)}</td><td></td></tr>
                                <tr class="total-row"><td>TOTAL</td><td class="currency">RS</td><td class="money">${amount(report.total)}</td><td></td></tr>
                            </tbody>
                        </table>
                        <div class="travel-line"><strong>TRAVEL BY</strong><span>${escapeHtml(formatStatus(report.travelBy) === "-" ? "" : formatStatus(report.travelBy))}</span></div>
                        <div class="travel-line"><strong>REMARK</strong><span>${escapeHtml(report.remarks)}</span></div>
                    </div>
                    <table class="visit-table">
                        <thead><tr><th>Date</th><th>Name / Visit Type</th><th>School / Party Name</th><th>City</th><th>Visited For</th><th>Assessment of Visit</th></tr></thead>
                        <tbody>${visitRows(report.visits)}</tbody>
                    </table>
                    <footer class="office-use"><div></div><div><strong>FOR OFFICE USE ONLY</strong><p>CHECKED BY: __________________________________</p><p>REMARKS FOR WORKING: __________________________</p></div></footer>
                    <div class="page-number">Page ${index + 1} of ${reports.length}</div>
                </section>
            `).join("");

            const printWindow = window.open("", "_blank", "width=1100,height=800");
            if (!printWindow) {
                showError("Please allow pop-ups to print or save the Expense Report as PDF.");
                return;
            }
            printWindow.document.open();
            printWindow.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Expense Report</title><style>
                @page{size:A4 portrait;margin:12mm}*{box-sizing:border-box}body{font-family:Arial,Helvetica,sans-serif;color:#222;font-size:10px;margin:0}.report-page{position:relative;min-height:270mm;page-break-after:always;padding-bottom:28mm}.report-page:last-child{page-break-after:auto}.company-header{display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid #777;padding-bottom:8px}.logo-word{font-family:Georgia,serif;font-size:25px;font-weight:bold;letter-spacing:3px;color:#555}.company-name{font-size:16px;font-weight:bold;text-align:right}h1{text-align:center;font-size:16px;margin:14px 0 10px}.meta-box{border:1px solid #555;padding:10px 12px;margin-bottom:10px}.meta-line{display:grid;grid-template-columns:130px 1fr 40px 100px;gap:8px;align-items:center;margin-bottom:12px}.expense-table,.visit-table{width:100%;border-collapse:collapse;table-layout:fixed}.expense-table td,.visit-table th,.visit-table td{border:1px solid #555;padding:6px 7px;vertical-align:top;overflow-wrap:anywhere}.expense-table td:first-child{width:34%}.expense-table .currency{width:7%;text-align:center}.expense-table .money{width:23%;text-align:right;font-weight:bold}.expense-table td:last-child{width:36%}.total-row{font-weight:bold}.travel-line{display:grid;grid-template-columns:120px 1fr;gap:8px;margin:8px 0 0;padding-left:2px}.visit-table{margin-top:12px}.visit-table th{background:#eee;text-align:left;font-weight:bold}.visit-table th:nth-child(1){width:12%}.visit-table th:nth-child(2){width:16%}.visit-table th:nth-child(3){width:22%}.visit-table th:nth-child(4){width:12%}.visit-table th:nth-child(5){width:16%}.visit-table th:nth-child(6){width:22%}.empty{text-align:center;color:#666}.office-use{position:absolute;bottom:8mm;left:0;right:0;min-height:25mm;border:1px solid #555;display:grid;grid-template-columns:40% 60%}.office-use>div{padding:9px;border-right:1px solid #555}.office-use>div:last-child{border-right:0}.office-use p{margin:8px 0 0}.page-number{position:absolute;bottom:0;right:0;font-size:9px}@media print{body{print-color-adjust:exact;-webkit-print-color-adjust:exact}}
            </style></head><body>${pages}<script>window.onload=()=>{window.focus();window.print();};</script></body></html>`);
            printWindow.document.close();
        } catch (error) {
            console.error("Expense PDF export failed:", error);
            showError(error?.response?.data?.message || error?.message || "Unable to export Expense Report PDF.");
        } finally {
            setLoading(false);
        }
    }

    /*
    |--------------------------------------------------------------------------
    | CSV EXPORT
    |--------------------------------------------------------------------------
    */

    function exportCsv() {
        if (!records.length) {
            showError(
                "There are no records to export."
            );

            return;
        }

        const keys =
            Array.from(
                new Set(
                    records.flatMap(
                        (record) =>
                            Object.keys(
                                record
                            )
                    )
                )
            );

        const cellValue = (
            record,
            key
        ) => {
            const value =
                record[key];

            if (value == null) {
                return "";
            }

            if (
                typeof value ===
                "object"
            ) {
                return (
                    value.name ??
                    value.title ??
                    JSON.stringify(
                        value
                    )
                );
            }

            return String(value);
        };

        const escapeCsv =
            (value) =>
                `"${String(
                    value
                ).replace(
                    /"/g,
                    '""'
                )}"`;

        const csv = [
            keys
                .map(
                    escapeCsv
                )
                .join(","),
            ...records.map(
                (record) =>
                    keys
                        .map(
                            (key) =>
                                escapeCsv(
                                    cellValue(
                                        record,
                                        key
                                    )
                                )
                        )
                        .join(",")
            ),
        ].join("\r\n");

        const blob =
            new Blob(
                [
                    "\uFEFF",
                    csv,
                ],
                {
                    type: "text/csv;charset=utf-8;",
                }
            );

        const url =
            URL.createObjectURL(
                blob
            );

        const link =
            document.createElement(
                "a"
            );

        link.href = url;

        link.download =
            `${reportType}_${new Date()
                .toISOString()
                .slice(
                    0,
                    10
                )}.csv`;

        document.body.appendChild(
            link
        );

        link.click();

        link.remove();

        URL.revokeObjectURL(
            url
        );
    }


    const showDailyFilters =
        reportType ===
        "daily_working";

    const showVisitFilters =
        reportType ===
        "visits";


    const handleApproveExpense = async (expense) => {
        if (!canAdminEditExpense) {
            showError("Only Admin can approve expenses.");
            return;
        }

        if (!expense?.id) {
            showError("Expense ID is missing. Please reload the report.");
            return;
        }

        if (String(expense?.status ?? "").toLowerCase() !== "submitted") {
            showError("Only submitted expenses can be approved.");
            return;
        }

        const defaultAmount = Number(expense?.claimedAmount ?? 0);
        const amountInput = window.prompt(
            "Enter approved amount",
            Number.isFinite(defaultAmount) ? defaultAmount.toFixed(2) : "0.00"
        );

        if (amountInput === null) return;

        const amount = Number(amountInput);
        if (amountInput.trim() === "" || !Number.isFinite(amount) || amount < 0) {
            showError("Please enter a valid approved amount.");
            return;
        }

        try {
            await approveExpense(expense.id, {
                approvedAmount: amount.toFixed(2),
            });
            await reloadDailyWorkingDetails();
        } catch (error) {
            showError(
                error?.response?.data?.message ||
                error?.message ||
                "Unable to approve expense."
            );
        }
    };

    const handleApproveAllExpenses = async (expensesToApprove) => {
        if (!canAdminEditExpense) {
            showError("Only Admin can approve expenses.");
            return;
        }

        const pending = (expensesToApprove || []).filter(
            (expense) =>
                expense?.id &&
                String(expense?.status ?? "").trim().toLowerCase() === "submitted"
        );

        if (!pending.length) {
            showError("There are no submitted expenses to approve.");
            return;
        }

        if (!window.confirm(
            `Approve all ${pending.length} submitted expenses using their claimed amounts?`
        )) {
            return;
        }

        setBulkApproving(true);
        const failures = [];

        try {
            // Use the existing single-expense API to preserve current backend behavior.
            for (const expense of pending) {
                const amount = Number(expense?.claimedAmount ?? 0);

                if (!Number.isFinite(amount) || amount < 0) {
                    failures.push(expense.id);
                    continue;
                }

                try {
                    await approveExpense(expense.id, {
                        approvedAmount: amount.toFixed(2),
                    });
                } catch (error) {
                    console.error(`Failed to approve expense ${expense.id}:`, error);
                    failures.push(expense.id);
                }
            }

            await reloadDailyWorkingDetails();

            if (failures.length) {
                showError(
                    `${pending.length - failures.length} expense(s) approved; ` +
                    `${failures.length} failed. Retry the failed expenses individually.`
                );
            }
        } catch (error) {
            showError(
                error?.response?.data?.message ||
                error?.message ||
                "Approval finished, but the report could not be refreshed."
            );
        } finally {
            setBulkApproving(false);
        }
    };

    const handleEditExpense = (expense) => {
        if (!canAdminEditExpense) {
            showError("Only Admin can edit expenses.");
            return;
        }

        if (!expense?.id) {
            showError("Expense ID is missing. Please reload the report.");
            return;
        }

        setEditingExpense(expense);
        setExpenseEditOpen(true);
    };

    const reloadDailyWorkingDetails = async () => {
        const employeeId =
            dailyWorkingDetails?.employeeId ?? dailyWorkingDetails?.employee?.id;
        const date = dailyWorkingDetails?.date;

        if (!employeeId || !date) {
            throw new Error("Employee ID or report date is missing.");
        }

        const response = await getDailyWorkingDetails(employeeId, date);
        const root = response?.data ?? response;
        const payload = root?.data ?? root;

        if (!payload || typeof payload !== "object") {
            throw new Error("Unable to refresh daily working details.");
        }

        setDailyWorkingDetails(payload);
    };

    /*
    |--------------------------------------------------------------------------
    | DAILY WORKING DETAILS VIEW
    |--------------------------------------------------------------------------
    */

    if (
        reportType ===
            "daily_working" &&
        dailyWorkingDetails
    ) {
        return (
            <div className="mt-5 space-y-5">
                <DailyWorkingDetails
                    details={dailyWorkingDetails}
                    loading={dailyWorkingDetailsLoading}
                    onBack={() => setDailyWorkingDetails(null)}
                    canEditExpenses={canAdminEditExpense}
                    onEditExpense={handleEditExpense}
                    onApproveExpense={handleApproveExpense}
                    onApproveAllExpenses={handleApproveAllExpenses}
                    bulkApproving={bulkApproving}
                />

                {canAdminEditExpense && (
                    <ExpenseModal
                        open={expenseEditOpen}
                        item={editingExpense}
                        viewMode={false}
                        onClose={() => {
                            setExpenseEditOpen(false);
                            setEditingExpense(null);
                        }}
                        reload={reloadDailyWorkingDetails}
                    />
                )}
            </div>
        );
    }


    /*
    |--------------------------------------------------------------------------
    | MAIN UI
    |--------------------------------------------------------------------------
    */

    return (
        <div className="mt-5 space-y-5">

            {/* Page Header */}
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
                        Reports &amp; Statements
                    </h1>

                    <p className="mt-1 text-sm text-gray-500">
                        Generate operational, financial and performance reports.
                    </p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                    <select
                        value={
                            reportType
                        }
                        onChange={(
                            event
                        ) =>
                            changeReportType(
                                event
                                    .target
                                    .value
                            )
                        }
                        className={`${INPUT_CLASS} mt-0 min-w-[240px]`}
                    >
                        {REPORTS.map(
                            (
                                report
                            ) => (
                                <option
                                    key={
                                        report.key
                                    }
                                    value={
                                        report.key
                                    }
                                >
                                    {
                                        report.label
                                    }
                                </option>
                            )
                        )}
                    </select>

                    <button
                        type="button"
                        onClick={() =>
                            setStatementOpen(
                                true
                            )
                        }
                        className="h-11 rounded-lg bg-brand-500 px-4 text-sm font-semibold text-white hover:bg-brand-600"
                    >
                        Generate Statement
                    </button>
                </div>
            </div>


            {/* Filters */}
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-navy-700 dark:bg-navy-800">

                <h2 className="text-base font-semibold text-navy-700 dark:text-white">
                    Report Filters
                </h2>

                <p className="mt-1 text-sm text-gray-500">
                    Select the required filters and generate your report.
                </p>

                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">

                    {/* From Date */}
                    <label
                        className={
                            LABEL_CLASS
                        }
                    >
                        From Date

                        <input
                            type="date"
                            value={
                                filters.fromDate
                            }
                            onChange={(
                                event
                            ) =>
                                updateFilter(
                                    "fromDate",
                                    event
                                        .target
                                        .value
                                )
                            }
                            className={
                                INPUT_CLASS
                            }
                        />
                    </label>


                    {/* To Date */}
                    <label
                        className={
                            LABEL_CLASS
                        }
                    >
                        To Date

                        <input
                            type="date"
                            min={
                                filters.fromDate ||
                                undefined
                            }
                            value={
                                filters.toDate
                            }
                            onChange={(
                                event
                            ) =>
                                updateFilter(
                                    "toDate",
                                    event
                                        .target
                                        .value
                                )
                            }
                            className={
                                INPUT_CLASS
                            }
                        />
                    </label>


                    {/* Employee */}
                    <label
                        className={
                            LABEL_CLASS
                        }
                    >
                        Employee Name

                        <select
                            value={
                                filters.employeeId
                            }
                            onChange={(
                                event
                            ) =>
                                handleEmployeeChange(
                                    event
                                        .target
                                        .value
                                )
                            }
                            disabled={
                                employeeLoading
                            }
                            className={
                                INPUT_CLASS
                            }
                        >
                            <option value="">
                                {employeeLoading
                                    ? "Loading employees..."
                                    : "All Employees"}
                            </option>

                            {employees.map(
                                (
                                    employee
                                ) => (
                                    <option
                                        key={String(
                                            employee.id
                                        )}
                                        value={String(
                                            employee.id
                                        )}
                                    >
                                        {getEmployeeName(
                                            employee
                                        )}
                                    </option>
                                )
                            )}
                        </select>
                    </label>


                    {/* Status */}
                    <label
                        className={
                            LABEL_CLASS
                        }
                    >
                        Status

                        <select
                            value={
                                filters.status
                            }
                            onChange={(
                                event
                            ) =>
                                updateFilter(
                                    "status",
                                    event
                                        .target
                                        .value
                                )
                            }
                            className={
                                INPUT_CLASS
                            }
                        >
                            <option value="">
                                All Statuses
                            </option>

                            <option value="planned">
                                Planned
                            </option>

                            <option value="started">
                                Started
                            </option>

                            <option value="submitted">
                                Submitted
                            </option>

                            <option value="approved">
                                Approved
                            </option>

                            <option value="rejected">
                                Rejected
                            </option>

                            <option value="completed">
                                Completed
                            </option>
                        </select>
                    </label>


                    {/* Daily Working specific filters */}
                    {showDailyFilters && (
                        <>
                            {/* Journey */}
                            <label
                                className={
                                    LABEL_CLASS
                                }
                            >
                                Journey Name

                                <select
                                    value={
                                        filters.journeyPlanItemId
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleJourneyChange(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                    disabled={
                                        !filters.employeeId ||
                                        journeyLoading
                                    }
                                    className={
                                        INPUT_CLASS
                                    }
                                >
                                    <option value="">
                                        {!filters.employeeId
                                            ? "Select Employee First"
                                            : journeyLoading
                                            ? "Loading journeys..."
                                            : "All Journeys"}
                                    </option>

                                    {journeys.map(
                                        (
                                            journey
                                        ) => (
                                            <option
                                                key={
                                                    journey.id
                                                }
                                                value={
                                                    journey.id
                                                }
                                            >
                                                {getJourneyName(
                                                    journey
                                                )}

                                                {journey.planTitle
                                                    ? ` — ${journey.planTitle}`
                                                    : ""}
                                            </option>
                                        )
                                    )}
                                </select>
                            </label>


                            {/* Visit */}
                            <label
                                className={
                                    LABEL_CLASS
                                }
                            >
                                Visit Name

                                <select
                                    value={
                                        filters.visitId
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        updateFilter(
                                            "visitId",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                    disabled={
                                        !filters.journeyPlanItemId ||
                                        visitLoading
                                    }
                                    className={
                                        INPUT_CLASS
                                    }
                                >
                                    <option value="">
                                        {!filters.journeyPlanItemId
                                            ? "Select Journey First"
                                            : visitLoading
                                            ? "Loading visits..."
                                            : "All Visits"}
                                    </option>

                                    {visits.map(
                                        (
                                            visit
                                        ) => (
                                            <option
                                                key={String(
                                                    visit.id
                                                )}
                                                value={String(
                                                    visit.id
                                                )}
                                            >
                                                {getVisitName(
                                                    visit
                                                )}
                                            </option>
                                        )
                                    )}
                                </select>
                            </label>


                            {/* Expense Type */}
                            <label
                                className={
                                    LABEL_CLASS
                                }
                            >
                                Expense Type

                                <select
                                    value={
                                        filters.expenseType
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        updateFilter(
                                            "expenseType",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                    className={
                                        INPUT_CLASS
                                    }
                                >
                                    <option value="">
                                        All Expense Types
                                    </option>

                                    <option value="travel">
                                        Travel
                                    </option>

                                    <option value="food">
                                        Food
                                    </option>

                                    <option value="stay">
                                        Stay
                                    </option>

                                    <option value="fuel">
                                        Fuel
                                    </option>

                                    <option value="phone">
                                        Phone
                                    </option>

                                    <option value="other">
                                        Other
                                    </option>
                                </select>
                            </label>


                            {/* Approval */}
                            <label
                                className={
                                    LABEL_CLASS
                                }
                            >
                                Expense Approval Status

                                <select
                                    value={
                                        filters.approvalStatus
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        updateFilter(
                                            "approvalStatus",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                    className={
                                        INPUT_CLASS
                                    }
                                >
                                    <option value="">
                                        All Approval Statuses
                                    </option>

                                    <option value="pending">
                                        Pending
                                    </option>

                                    <option value="submitted">
                                        Submitted
                                    </option>

                                    <option value="approved">
                                        Approved
                                    </option>

                                    <option value="rejected">
                                        Rejected
                                    </option>

                                    <option value="paid">
                                        Paid
                                    </option>
                                </select>
                            </label>
                        </>
                    )}


                    {/* Visit specific filters */}
                    {showVisitFilters && (
                        <>
                            <label
                                className={
                                    LABEL_CLASS
                                }
                            >
                                School ID

                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={
                                        filters.schoolId
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        updateFilter(
                                            "schoolId",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                    className={
                                        INPUT_CLASS
                                    }
                                    placeholder="Enter school ID"
                                />
                            </label>


                            <label
                                className={
                                    LABEL_CLASS
                                }
                            >
                                Party ID

                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={
                                        filters.partyId
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        updateFilter(
                                            "partyId",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                    className={
                                        INPUT_CLASS
                                    }
                                    placeholder="Enter party ID"
                                />
                            </label>
                        </>
                    )}
                </div>


                {/* Buttons */}
                <div className="mt-5 flex flex-wrap gap-3">

                    <button
                        type="button"
                        onClick={
                            loadReport
                        }
                        disabled={
                            loading
                        }
                        className="h-11 rounded-lg bg-brand-500 px-5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
                    >
                        {loading
                            ? "Generating..."
                            : "Generate Report"}
                    </button>


                    <button
                        type="button"
                        onClick={
                            resetFilters
                        }
                        disabled={
                            loading
                        }
                        className="h-11 rounded-lg border border-gray-300 px-5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-navy-600 dark:text-white"
                    >
                        Reset
                    </button>


                    <button
                        type="button"
                        onClick={exportExcel}
                        disabled={!hasGenerated || !records.length || loading}
                        className="h-11 rounded-lg border border-gray-300 px-5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-navy-600 dark:text-white"
                    >
                        {loading ? "Exporting..." : "Export Excel"}
                    </button>

                    {reportType === "expenses" && (
                        <button
                            type="button"
                            onClick={exportExpenseReportPdf}
                            disabled={!hasGenerated || !records.length || loading}
                            className="h-11 rounded-lg border border-red-200 bg-red-50 px-5 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300"
                        >
                            {loading ? "Preparing PDF..." : "Export Expense PDF"}
                        </button>
                    )}
                </div>
            </section>


            {/* Summary cards */}
            {hasGenerated &&
                Object.keys(
                    summary
                ).length > 0 && (
                    <section className="grid grid-cols-2 gap-4 md:grid-cols-4">
                        {Object.entries(
                            summary
                        ).map(
                            (
                                [
                                    key,
                                    value,
                                ]
                            ) => (
                                <div
                                    key={
                                        key
                                    }
                                    className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-navy-700 dark:bg-navy-800"
                                >
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {key
                                            .replace(
                                                /([A-Z])/g,
                                                " $1"
                                            )
                                            .replace(
                                                /_/g,
                                                " "
                                            )
                                            .replace(
                                                /^./,
                                                (
                                                    char
                                                ) =>
                                                    char.toUpperCase()
                                            )}
                                    </p>

                                    <p className="mt-2 text-xl font-bold text-navy-700 dark:text-white">
                                        {typeof value ===
                                        "number"
                                            ? value.toLocaleString(
                                                  "en-IN"
                                              )
                                            : String(
                                                  value ??
                                                      0
                                              )}
                                    </p>
                                </div>
                            )
                        )}
                    </section>
                )}


            {/* =========================================================
                DAILY WORKING SUMMARY TABLE
            ========================================================= */}
            {reportType ===
            "daily_working" ? (
                <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-navy-700 dark:bg-navy-800">

                    <div className="border-b border-gray-200 px-5 py-4 dark:border-navy-700">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h2 className="text-base font-bold text-navy-700 dark:text-white">
                                    Daily Working Report
                                </h2>

                                <p className="mt-1 text-xs text-gray-500">
                                    Employee and date wise working summary.
                                </p>
                            </div>

                            <div className="flex flex-wrap gap-3 text-xs">
                                <span className="flex items-center gap-2">
                                    <span className="h-3 w-3 rounded-sm bg-yellow-300" />
                                    Team Leader Reviewed
                                </span>
                                <span className="flex items-center gap-2">
                                    <span className="h-3 w-3 rounded-sm bg-green-300" />
                                    Admin Approved
                                </span>
                                <span className="flex items-center gap-2">
                                    <span className="h-3 w-3 rounded-sm bg-red-300" />
                                    Pending
                                </span>
                            </div>
                        </div>
                    </div>


                    <div className="overflow-x-auto">
                        <table className="min-w-max w-full text-sm">

                            <thead>
                                <tr className="border-b border-gray-200 bg-gray-50 dark:border-navy-700 dark:bg-navy-900">
                                    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Date
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Employee
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Employee Code
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Headquarter
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Destination
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Local Working DA (₹ / Day)
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Outstation Fooding (₹ / Day)
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Outstation Lodging (₹ / Day)
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Same Day Return (₹)
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Local Two Wheeler (₹ / KM)
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Outstation Two Wheeler (₹ / KM)
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Local Car (₹ / KM)
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Outstation Car (₹ / KM)
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Mobile Bill + Internet (₹ / Month)
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Current Visit / Location
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Latitude
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Longitude
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Total Visits
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Claimed Expense
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Approved Expense
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Approval
                                    </th>

                                    <th className="whitespace-nowrap px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Action
                                    </th>
                                </tr>
                            </thead>


                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td
                                            colSpan={
                                                21
                                            }
                                            className="px-4 py-10 text-center text-sm text-gray-500"
                                        >
                                            Loading Daily Working Report...
                                        </td>
                                    </tr>
                                ) : records.length ===
                                  0 ? (
                                    <tr>
                                        <td
                                            colSpan={
                                                21
                                            }
                                            className="px-4 py-10 text-center text-sm text-gray-500"
                                        >
                                            {hasGenerated
                                                ? "No Data Found"
                                                : "Select filters and generate the report."}
                                        </td>
                                    </tr>
                                ) : (
                                    records.map(
                                        (
                                            row
                                        ) => {
                                            // The backend approvalColor/approvalStatus is based on persisted
                                            // approvedByRole. Remarks are comments, not approval evidence.
                                            const normalizedApprovalStatus = String(
                                                row?.approvalStatus ?? row?.approvalColor ?? ""
                                            ).trim().toLowerCase().replace(/[ -]+/g, "_");

                                            const isAdminApproved =
                                                normalizedApprovalStatus === "admin_approved" ||
                                                normalizedApprovalStatus === "green" ||
                                                row?.allExpensesAdminUpdated === true;

                                            const hasTeamLeaderReview =
                                                normalizedApprovalStatus === "team_leader_approved" ||
                                                normalizedApprovalStatus === "team_leader_reviewed" ||
                                                normalizedApprovalStatus === "tl_approved" ||
                                                normalizedApprovalStatus === "yellow" ||
                                                row?.allExpensesTeamLeaderReviewed === true;

                                            const rowColorClass = isAdminApproved
                                                ? "border-b border-green-200 bg-green-100 hover:bg-green-200"
                                                : hasTeamLeaderReview
                                                    ? "border-b border-yellow-200 bg-yellow-100 hover:bg-yellow-200"
                                                    : "border-b border-red-200 bg-red-100 hover:bg-red-200";

                                            return (
                                                <tr
                                                    key={row.id}
                                                    className={rowColorClass}
                                                >
                                                    <td className="whitespace-nowrap px-4 py-4 font-medium text-gray-800">
                                                        {formatDate(
                                                            row?.date
                                                        )}
                                                    </td>

                                                    <td className="whitespace-nowrap px-4 py-4 font-semibold text-gray-800">
                                                        {displayValue(
                                                            row?.employee
                                                                ?.name
                                                        )}
                                                    </td>

                                                    <td className="whitespace-nowrap px-4 py-4 text-gray-700">
                                                        {displayValue(
                                                            row?.employee
                                                                ?.employeeCode
                                                        )}
                                                    </td>

                                                    {/* Employee Headquarter */}
                                                    <td className="whitespace-nowrap px-4 py-4 text-gray-700">
                                                        {displayValue(
                                                            getFirstValue(
                                                                row?.employee?.headquarter?.name,
                                                                row?.employee?.headquarter,
                                                                row?.employee?.headquarters?.name,
                                                                row?.employee?.hq?.name,
                                                                row?.employee?.hq,
                                                                row?.employee?.headQuarter,
                                                                row?.employee?.headquarterName,
                                                                row?.employee?.headquartersName
                                                            )
                                                        )}
                                                    </td>

                                                 
                                                    {/* Destination / Visit Location */}
<td className="whitespace-nowrap px-4 py-4 text-gray-700">
  {displayValue(
    row?.currentVisitDetails?.visitLocation ??
    row?.visitDetails?.[0]?.visitLocation ??
    row?.currentVisit ??
    "N/A"
  )}
</td>

                                                    {/* Assigned Local TA/DA */}
                                                    <td className="px-4 py-3">
                                                        {formatAmount(
                                                            row?.localTaDa ??
                                                            row?.employee?.taDa?.daLocalWorking ??
                                                            0
                                                        )}
                                                    </td>

                                                    {/* Outstation Fooding */}
                                                    <td className="whitespace-nowrap px-4 py-3 text-right">
                                                        {formatAmount(
                                                            row?.outstationFooding ??
                                                            row?.employee?.taDa?.daOutstationFooding ??
                                                            row?.outstationTaDa ??
                                                            0
                                                        )}
                                                    </td>

                                                    {/* Outstation Lodging */}
                                                    <td className="whitespace-nowrap px-4 py-3 text-right">
                                                        {formatAmount(
                                                            row?.outstationLodging ??
                                                            row?.employee?.taDa?.daOutstationLodging ??
                                                            0
                                                        )}
                                                    </td>

                                                    {/* Same Day Return */}
                                                    <td className="whitespace-nowrap px-4 py-3 text-right">
                                                        {formatAmount(
                                                            row?.sameDayReturn ??
                                                            row?.employee?.taDa?.sameDayReturn ??
                                                            0
                                                        )}
                                                    </td>

                                                    {/* Local Two Wheeler */}
                                                    <td className="whitespace-nowrap px-4 py-3 text-right">
                                                        {formatAmount(
                                                            row?.localTwoWheeler ??
                                                            row?.employee?.taDa?.localTwoWheeler ??
                                                            0
                                                        )}
                                                    </td>

                                                    {/* Outstation Two Wheeler */}
                                                    <td className="whitespace-nowrap px-4 py-3 text-right">
                                                        {formatAmount(
                                                            row?.outstationTwoWheeler ??
                                                            row?.employee?.taDa?.outstationTwoWheeler ??
                                                            0
                                                        )}
                                                    </td>

                                                    {/* Local Car */}
                                                    <td className="whitespace-nowrap px-4 py-3 text-right">
                                                        {formatAmount(
                                                            row?.localCar ??
                                                            row?.employee?.taDa?.localCar ??
                                                            0
                                                        )}
                                                    </td>

                                                    {/* Outstation Car */}
                                                    <td className="whitespace-nowrap px-4 py-3 text-right">
                                                        {formatAmount(
                                                            row?.outstationCar ??
                                                            row?.employee?.taDa?.outstationCar ??
                                                            0
                                                        )}
                                                    </td>

                                                    {/* Mobile Bill + Internet */}
                                                    <td className="whitespace-nowrap px-4 py-3 text-right">
                                                        {formatAmount(
                                                            row?.mobileBillInternet ??
                                                            row?.employee?.taDa?.mobileBillInternet ??
                                                            row?.employee?.taDa?.mobileInternet ??
                                                            0
                                                        )}
                                                    </td>

                                                    {/* Latest visit location */}
                                                    <td className="min-w-[220px] px-4 py-4 text-gray-700">
                                                        {(() => {
                                                            const { visit, latitude, longitude } = getVisitCoordinates(row);
                                                            const locationName = getFirstValue(
                                                                visit?.address,
                                                                visit?.location,
                                                                visit?.school?.name,
                                                                visit?.party?.name,
                                                                row?.currentLocation?.address,
                                                                row?.currentLocation?.name
                                                            );

                                                            if (latitude != null && longitude != null) {
                                                                return (
                                                                    <ReverseGeocodedLocation
                                                                        latitude={latitude}
                                                                        longitude={longitude}
                                                                        fallback={locationName || ""}
                                                                    />
                                                                );
                                                            }

                                                            return displayValue(locationName);
                                                        })()}
                                                    </td>

                                                    <td className="whitespace-nowrap px-4 py-4 text-gray-700">
                                                        {displayValue(getVisitCoordinates(row).latitude)}
                                                    </td>

                                                    <td className="whitespace-nowrap px-4 py-4 text-gray-700">
                                                        {displayValue(getVisitCoordinates(row).longitude)}
                                                    </td>

                                                    <td className="px-4 py-4 text-center font-semibold text-gray-800">
                                                        {Number(
                                                            row?.totalVisits ||
                                                                0
                                                        )}
                                                    </td>

                                                    <td className="whitespace-nowrap px-4 py-4 text-right font-semibold text-gray-800">
                                                        {formatAmount(
                                                            row?.claimedExpense
                                                        )}
                                                    </td>

                                                    <td className="whitespace-nowrap px-4 py-4 text-right font-semibold text-gray-800">
                                                        {formatAmount(
                                                            row?.approvedExpense
                                                        )}
                                                    </td>

                                                    <td className="px-4 py-4 text-center">
                                                        <span
                                                            className={`font-bold ${
                                                                isAdminApproved
                                                                    ? "text-green-800"
                                                                    : hasTeamLeaderReview
                                                                        ? "text-yellow-800"
                                                                        : "text-red-800"
                                                            }`}
                                                        >
                                                            {isAdminApproved
                                                                ? "Admin Approved"
                                                                : hasTeamLeaderReview
                                                                    ? "Team Leader Reviewed"
                                                                    : "Pending"}
                                                        </span>
                                                    </td>

                                                    <td className="px-4 py-4 text-center">
                                                        <button
                                                            type="button"
                                                            onClick={(event) => {
                                                                event.preventDefault();
                                                                event.stopPropagation();

                                                                handleDailyWorkingView(row);
                                                            }}
                                                            disabled={dailyWorkingDetailsLoading}
                                                            className="rounded-lg bg-white px-3 py-2 text-xs font-semibold text-brand-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                                        >
                                                            {dailyWorkingDetailsLoading
                                                                ? "Loading..."
                                                                : "View"}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            ) : (
                /* =====================================================
                   EXISTING REPORTS
                ===================================================== */
                <DataTable
                    title={
                        selectedReport?.label ||
                        "Report"
                    }
                    columns={
                        reportType ===
                        "visits"
                            ? VISIT_COLUMNS
                            : reportColumns(
                                  reportType
                              )
                    }
                    data={
                        records
                    }
                    loading={
                        loading
                    }
                    addButtonText=""
                />
            )}


            {/* Statement History */}
            <div className="mt-8">
                <StatementHistory
                    key={
                        statementRefreshKey
                    }
                />
            </div>


            {/* Statement Modal */}
            <StatementModal
                open={
                    statementOpen
                }
                onClose={() =>
                    setStatementOpen(
                        false
                    )
                }
                reload={() => {
                    setStatementOpen(
                        false
                    );

                    setStatementRefreshKey(
                        (
                            previous
                        ) =>
                            previous +
                            1
                    );
                }}
            />
        </div>
    );
}